import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getPropertyById,
  updateProperty,
  type PropertyPayload,
} from "../../imoveis/imoveisApi";
import { APP_LOCALE } from "../../config/locale";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para editar imóveis.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível atualizar o imóvel.";
};

const parseLoadError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar imóveis.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar o imóvel.";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

export function EditarImovelView() {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const [nomeImovel, setNomeImovel] = useState("");
  const [morada, setMorada] = useState("");
  const [tipologia, setTipologia] = useState("");
  const [area, setArea] = useState("");
  const [renda, setRenda] = useState("");
  const [imi, setImi] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState<string | null>(null);
  const [propertySnapshotId, setPropertySnapshotId] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadProperty = async () => {
      if (!propertyId) {
        setLoadError("Imóvel inválido.");
        setIsLoading(false);
        return;
      }

      const parsedId = Number(propertyId);
      if (Number.isNaN(parsedId)) {
        setLoadError("Imóvel inválido.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setSubmitError(null);

      try {
        const response = await getPropertyById(parsedId);
        if (!isMounted) return;

        setPropertySnapshotId(response.id);
        setNomeImovel(response.name ?? "");
        setMorada(response.address ?? "");
        setTipologia(response.propertyType ?? "");
        setArea(response.area !== null && response.area !== undefined ? String(response.area) : "");
        setRenda(response.rentValue !== null && response.rentValue !== undefined ? String(response.rentValue) : "");
        setImi(response.annualImi !== null && response.annualImi !== undefined ? String(response.annualImi) : "");
        setLastUpdatedLabel(formatDate(response.lastModified));
      } catch (error) {
        if (!isMounted) return;
        setLoadError(parseLoadError(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProperty();

    return () => {
      isMounted = false;
    };
  }, [propertyId]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!propertySnapshotId) {
      setSubmitError("Imóvel inválido.");
      return;
    }

    if (!nomeImovel.trim()) {
      setSubmitError("O nome/identificador do imóvel é obrigatório.");
      return;
    }

    setIsSaving(true);

    const payload: PropertyPayload = {
      name: nomeImovel.trim(),
      address: morada.trim() || null,
      propertyType: tipologia.trim() || null,
      area: area !== "" ? parseFloat(area) : null,
      rentValue: renda !== "" ? parseFloat(renda) : null,
      annualImi: imi !== "" ? parseFloat(imi) : null,
    };

    try {
      await updateProperty(propertySnapshotId, payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    navigate("/imoveis", {
      state: { toast: "Imóvel atualizado com sucesso" },
    });
  };

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/imoveis")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "editar-imovel-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving || isLoading}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Alterações"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Editar Imóvel"
        subtitle="Atualize os dados do imóvel."
        actions={actions}
        backButton={{ onClick: () => navigate("/imoveis") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {loadError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {loadError}
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <div
            className="text-secondary-text mb-6"
            style={{
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-normal)",
            }}
          >
            Última atualização: {lastUpdatedLabel ?? "—"}
          </div>

          <form id="editar-imovel-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados do Imóvel
                  </h3>

                  <div>
                    <label
                      htmlFor="nomeImovel"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Identificador do Imóvel{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      id="nomeImovel"
                      type="text"
                      value={nomeImovel}
                      onChange={(event) => setNomeImovel(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="morada"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Morada
                    </label>
                    <input
                      id="morada"
                      type="text"
                      value={morada}
                      onChange={(event) => setMorada(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="tipologia"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Tipologia
                    </label>
                    <input
                      id="tipologia"
                      type="text"
                      value={tipologia}
                      onChange={(event) => setTipologia(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="area"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Área (m²)
                    </label>
                    <input
                      id="area"
                      type="number"
                      min="0"
                      step="0.01"
                      value={area}
                      onChange={(event) => setArea(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados Financeiros
                  </h3>

                  <div>
                    <label
                      htmlFor="renda"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Valor de Renda (€)
                    </label>
                    <input
                      id="renda"
                      type="number"
                      min="0"
                      step="0.01"
                      value={renda}
                      onChange={(event) => setRenda(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="imi"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      IMI Anual (€)
                    </label>
                    <input
                      id="imi"
                      type="number"
                      min="0"
                      step="0.01"
                      value={imi}
                      onChange={(event) => setImi(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
